<script lang="ts" setup>
import { ref, type VNodeRef } from "vue";
import { ThemeButton, ThemePopoverMenu } from "@/ui-components";
import MenuIcon from "@/assets/icons/menu.svg?component";
import { useUiStore } from "@/stores/ui.ts";
import { INavMenuItem } from "@/types";
import ExternalLink from "@/components/MediaDetails/Modals/ExternalLink.vue";

const uiStore = useUiStore();
const { showOverlay } = uiStore;

const menuButtonRef = ref<VNodeRef | undefined>(undefined);
const linkListRef = ref<VNodeRef | undefined>(undefined);
const isExpanded = ref<boolean>(false);

const onApplyHide = () => {
  switchState(false);
  menuButtonRef.value?.$el.focus();
};

const onApplyShow = () => {
  switchState(true);

  const firstLink = linkListRef.value?.firstElementChild.querySelector("a");
  if (firstLink) {
    setTimeout(() => firstLink.focus(), 50);
  }
};

const switchState = (expandOverlay: boolean) => {
  isExpanded.value = expandOverlay;
  showOverlay([isExpanded.value, isExpanded.value]);
};

const props = defineProps<{
  mainItems: INavMenuItem[];
  supplementalItems: INavMenuItem[];
}>();
</script>

<template>
  <ThemePopoverMenu @apply-show="onApplyShow" @apply-hide="onApplyHide">
    <template #trigger="{ open }">
      <ThemeButton
        ref="menuButtonRef"
        variant="light"
        :fab="true"
        :icon-center="MenuIcon"
        aria-label="Menü"
        :aria-expanded="isExpanded"
        aria-controls="popover-menu"
        @click="open"
      />
    </template>
    <template #menu-content="{ close }">
      <div ref="linkListRef">
        <ul class="menu-items list-unstyled">
          <li v-for="(item, key) of mainItems" :key="key" class="menu-item">
            <template v-if="item.isExternal">
              <ExternalLink :url="item.url">
                <template #buttonOrLinkComponent>
                  <a class="d-flex" href="#">{{ item.label }}</a>
                </template>
              </ExternalLink>
            </template>
            <template v-else>
              <router-link v-slot="{ navigate, href }" :to="item.url" custom>
                <a
                  :href="href"
                  class="d-flex"
                  @click="
                    (event: MouseEvent) => {
                      navigate(event);
                      close();
                    }
                  "
                >
                  {{ item.label }}
                </a>
              </router-link>
            </template>
          </li>
        </ul>
        <ul class="menu-items menu-items-supplemental list-unstyled">
          <li v-for="(item, key) of supplementalItems" :key="key" class="menu-item">
            <template v-if="item.isExternal">
              <ExternalLink :url="item.url">
                <template #buttonOrLinkComponent>
                  <a class="d-flex" href="#">{{ item.label }}</a>
                </template>
              </ExternalLink>
            </template>
            <template v-else>
              <router-link v-slot="{ navigate, href }" :to="item.url" custom>
                <a
                  :href="href"
                  class="d-flex"
                  @click="
                    (event: MouseEvent) => {
                      navigate(event);
                      close();
                    }
                  "
                >
                  {{ item.label }}
                </a>
              </router-link>
            </template>
          </li>
        </ul>
      </div>
    </template>
  </ThemePopoverMenu>
</template>
